import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { PRINCIPALE, traduci, useTraduzioni } from '../../i18n';
import { angoliProposti, caricaImmagine, eFoto, eHeic, fotoRaddrizzata } from '../../utils/raddrizza';
import { trattieniFocus } from '../features/GalleriaFoto';
import './anteprima.css';
import './raddrizza.css';

// Anteprima della foto di un documento con i quattro angoli trovati (si spostano col dito o con le frecce), poi
// "Va bene" (raddrizzata), "Salta" (la foto com'è) o "Rifai" (di nuovo la fotocamera). Se la foto non si legge
// (es. un HEIC su Chrome) passa com'è, ma lo diciamo invece di far finta di niente.

// I due motivi per cui una foto passa com'è (l'italiano di base: lo staff e chi li importa li leggono così)
export const NON_LEGGIBILE = traduci(PRINCIPALE, 'documenti.raddrizza.non_leggibile');
export const HEIC_COSI = traduci(PRINCIPALE, 'documenti.raddrizza.heic');

const PASSO = 0.01; // di quanto si sposta un angolo con le frecce
const PASSO_GROSSO = 0.05; // con Maiusc

const Raddrizza = ({ file, onFatto: fatto, onRifai, onAnnulla, posizione = 1, totale = 1 }) => {
  const { t } = useTraduzioni();
  const chiuso = useRef(false);
  const onFatto = (f, motivo) => { if (!chiuso.current) { chiuso.current = true; fatto(f, motivo); } }; // una volta sola per foto
  const [img, setImg] = useState(null);
  const [angoli, setAngoli] = useState(null);
  const [trovato, setTrovato] = useState(true);
  const [attesa, setAttesa] = useState(false);
  const riquadro = useRef(null);
  const trascina = useRef(null);
  const finestra = useRef(null);
  const annulla = useRef(null);
  const annullaOra = useRef(onAnnulla);
  annullaOra.current = onAnnulla;

  // Finestra modale come l'anteprima: Tab e Maiusc+Tab restano dentro, Esc annulla, la pagina sotto non scorre, alla
  // chiusura il fuoco torna dove era (prima il Tab usciva sulla pagina, Esc non faceva niente e la pagina scorreva)
  useEffect(() => {
    const prima = document.activeElement;
    toast.dismiss?.(); // un avviso aperto in alto copriva "Annulla" e il sottotitolo al telefono
    annulla.current?.focus();
    const tasto = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); annullaOra.current?.(); } else trattieniFocus(e, finestra.current);
    };
    document.addEventListener('keydown', tasto);
    const scorre = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', tasto);
      document.body.style.overflow = scorre;
      if (prima && document.contains(prima)) prima.focus?.();
    };
  }, []);

  useEffect(() => {
    let vivo = true;
    let url = null;
    caricaImmagine(file).then((r) => {
      url = r.url;
      if (!vivo) return;
      const p = angoliProposti(r.img);
      setImg(r.img); setAngoli(p.angoli); setTrovato(p.trovato);
    }).catch(() => { if (vivo) onFatto(file, t(eHeic(file) ? 'documenti.raddrizza.heic' : 'documenti.raddrizza.non_leggibile')); });
    return () => { vivo = false; if (url) URL.revokeObjectURL(url); };
  }, [file]); // eslint-disable-line

  // Un angolo non esce mai dalla foto (né col dito né con le frecce): fuori non c'è niente da raddrizzare
  const sposta = useCallback((i, x, y) => setAngoli((a) => a.map((p, k) => (k === i
    ? [Math.max(0, Math.min(1, x)), Math.max(0, Math.min(1, y))] : p))), []);

  const muovi = useCallback((e) => {
    if (trascina.current === null || !riquadro.current) return;
    const r = riquadro.current.getBoundingClientRect();
    sposta(trascina.current, (e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
  }, [sposta]);

  const conFrecce = (e, i) => {
    const d = e.shiftKey ? PASSO_GROSSO : PASSO;
    const dove = { ArrowLeft: [-d, 0], ArrowRight: [d, 0], ArrowUp: [0, -d], ArrowDown: [0, d] }[e.key];
    if (!dove) return;
    e.preventDefault();
    sposta(i, angoli[i][0] + dove[0], angoli[i][1] + dove[1]);
  };

  const conferma = async () => {
    setAttesa(true);
    try { onFatto(await fotoRaddrizzata(img, angoli, file.name || 'foto.jpg')); } catch { onFatto(file); } finally { setAttesa(false); }
  };

  return (
    <div className="anteprima-doc raddrizza" ref={finestra} role="dialog" aria-modal="true" aria-label={t('documenti.raddrizza.titolo')} data-testid="raddrizza">
      <div className="testa-a">
        <b>
          {t('documenti.raddrizza.domanda')}
          {trovato
            ? <small>{t('documenti.raddrizza.spostate')}</small>
            : <small className="rd-avviso" data-testid="bordi-non-trovati">{t('documenti.raddrizza.bordi_non_trovati')}</small>}
          {totale > 1 && <small className="rd-quante" data-testid="quante-foto">{t('documenti.raddrizza.quante', { n: posizione, tot: totale })}</small>}
        </b>
        <button type="button" ref={annulla} onClick={onAnnulla}>{t('comune.annulla')}</button>
      </div>
      <div className="corpo">
        {img && angoli ? (
          <div className="rd-foto" ref={riquadro} onPointerMove={muovi} onPointerUp={() => { trascina.current = null; }} onPointerLeave={() => { trascina.current = null; }}
            style={{ aspectRatio: `${img.naturalWidth} / ${img.naturalHeight}`, '--ar': img.naturalWidth / img.naturalHeight }}>
            <img src={img.src} alt={t('documenti.raddrizza.foto_documento')} draggable={false} />
            <svg viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
              <polygon points={angoli.map(([x, y]) => `${x},${y}`).join(' ')} />
            </svg>
            {angoli.map(([x, y], i) => (
              <span key={i} className="rd-angolo" style={{ '--x': `${x * 100}%`, '--y': `${y * 100}%` }} role="slider"
                aria-label={t(['documenti.raddrizza.angolo_as', 'documenti.raddrizza.angolo_ad', 'documenti.raddrizza.angolo_bd', 'documenti.raddrizza.angolo_bs'][i])}
                aria-valuenow={Math.round(x * 100)} aria-valuemin={0} aria-valuemax={100}
                aria-valuetext={t('documenti.raddrizza.angolo_valore', { x: Math.round(x * 100), y: Math.round(y * 100) })}
                data-testid={`angolo-${i}`} tabIndex={0} onKeyDown={(e) => conFrecce(e, i)}
                onPointerDown={(e) => { e.preventDefault(); e.currentTarget.parentElement.setPointerCapture?.(e.pointerId); trascina.current = i; }} />
            ))}
          </div>
        ) : <p>{t('documenti.raddrizza.cerco')}</p>}
      </div>
      <div className="piede rd-bottoni">
        <button type="button" onClick={onRifai} disabled={attesa}>{t('documenti.raddrizza.rifai')}</button>
        <button type="button" onClick={() => onFatto(file)} disabled={attesa}>{t('documenti.raddrizza.salta')}</button>
        <button type="button" className="p" onClick={conferma} disabled={attesa || !img}>{t(attesa ? 'documenti.raddrizza.in_corso' : 'documenti.raddrizza.va_bene')}</button>
      </div>
    </div>
  );
};

// Coda di foto da raddrizzare una alla volta: apri(files) → per ognuna l'anteprima → onPronta(file, motivo).
// `motivo` c'è solo quando la foto non si è potuta raddrizzare (HEIC, file rovinato) e passa com'è.
export const useRaddrizza = (onPronta, onRifai, onAnnulla) => {
  const [coda, setCoda] = useState([]);
  const [fatte, setFatte] = useState(0);
  const apri = (files) => {
    const foto = files.filter(eFoto);
    if (foto.length) setCoda((c) => [...c, ...foto]);
    return foto;
  };
  // finita la coda il contatore riparte da zero, se no la volta dopo direbbe "Foto 4 di 4" alla prima foto
  const avanti = () => { setFatte((n) => (coda.length > 1 ? n + 1 : 0)); setCoda((c) => c.slice(1)); };
  const vuota = () => { setCoda([]); setFatte(0); };
  // Nel body: dentro una card con l'animazione (transform) il position:fixed resterebbe chiuso nella card
  const vista = coda.length ? createPortal(
    <Raddrizza key={`${coda[0].name}-${coda[0].size}-${coda[0].lastModified}-${fatte}`} file={coda[0]}
      posizione={fatte + 1} totale={fatte + coda.length}
      onFatto={(f, motivo) => { onPronta(f, motivo); avanti(); }}
      onAnnulla={() => { vuota(); onAnnulla?.(); }}
      onRifai={() => { avanti(); onRifai?.(); }} />,
    document.body,
  ) : null;
  return { apri, vista, inCorso: coda.length > 0 };
};

export default Raddrizza;
